// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  CreditCard, 
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Loader2,
  Ticket,
  Package,
  Calendar,
  FileText,
  UserCheck,
  Video
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface PurchasedItem {
  id: string;
  type: 'ticket' | 'extra';
  name: string;
  price: number;
  quantity: number;
  purchaser_name: string;
  purchaser_email: string;
  is_redeemed: boolean;
  redeemed_at?: string;
  qr_code: string;
  created_at: string;
  registration_id?: string;
  payment_status?: 'paid' | 'pending';
}

interface SpecialRequest {
  id: string;
  team_name: string;
  division: string;
  coach_name: string;
  coach_email: string;
  request_details: string;
  fee: number;
  payment_status: 'paid' | 'pending';
  created_at: string;
}

interface CoachRegistration {
  id: string;
  coach_name: string;
  coach_email: string;
  college_name: string;
  division: string;
  payment_status: 'paid' | 'pending';
  amount: number;
  created_at: string;
  filler_name?: string;
  filler_email?: string;
  additional_coaches?: string[];
  additional_coach_count?: number;
}

interface MediaCredential {
  id: string;
  name: string;
  email: string;
  organization: string;
  credential_type: string;
  status: 'approved' | 'pending' | 'denied';
  payment_status: 'paid' | 'pending' | 'free';
  amount_paid: number;
  created_at: string;
}

interface FinanceSummary {
  registrationRevenue: number;
  registrationPending: number;
  ticketRevenue: number;
  ticketPending: number;
  extrasRevenue: number;
  extrasPending: number;
  specialRequestRevenue: number;
  specialRequestPending: number;
  totalRevenue: number;
  totalPending: number;
  totalRegistrations: number;
  paidRegistrations: number;
  pendingRegistrations: number;
  ticketsSold: number;
  extrasSold: number;
}

const EventFinanceTab: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [purchasedItems, setPurchasedItems] = useState<PurchasedItem[]>([]);
  const [specialRequests, setSpecialRequests] = useState<SpecialRequest[]>([]);
  const [coachRegistrations, setCoachRegistrations] = useState<CoachRegistration[]>([]);
  const [mediaCredentials, setMediaCredentials] = useState<MediaCredential[]>([]);
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<PurchasedItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('items');

  useEffect(() => {
    if (eventId) {
      fetchFinanceData();
    }
  }, [eventId]);

  const fetchFinanceData = async () => {
    if (!eventId) return;
    
    setLoading(true);
    try {
      // Fetch from event_registrations (OLD table - has dedicated columns)
      const { data: oldRegs } = await supabase
        .from('event_registrations')
        .select('id, status, registration_data, created_at, registration_amount, extras_amount, special_request_amount, tickets_amount')
        .eq('event_id', eventId);

      // Fetch from registrations (NEW table - needs to parse pricing_breakdown)
      const { data: newRegs } = await supabase
        .from('registrations')
        .select('id, status, payment_status, pricing_breakdown, registrant_data, created_at')
        .eq('event_id', eventId);

      // Fetch purchased tickets with registration payment status
      const { data: tickets } = await supabase
        .from('purchased_tickets')
        .select('*, registrations:registration_id(payment_status, status)')
        .eq('event_id', eventId);

      // Fetch purchased extras with registration payment status
      const { data: extras } = await supabase
        .from('purchased_extras')
        .select('*, registrations:registration_id(payment_status, status)')
        .eq('event_id', eventId);

      // Calculate summary
      let registrationRevenuePaid = 0;
      let registrationRevenuePending = 0;
      let extrasRevenuePaid = 0;
      let extrasRevenuePending = 0;
      let ticketsRevenuePaid = 0;
      let ticketsRevenuePending = 0;
      let specialRequestRevenuePaid = 0;
      let specialRequestRevenuePending = 0;
      let paidCount = 0;
      let pendingCount = 0;

      // Process OLD registrations (event_registrations) - use dedicated columns
      oldRegs?.forEach(reg => {
        const regAmount = (reg as any).registration_amount ?? 0;
        const extrasAmount = (reg as any).extras_amount ?? 0;
        const ticketsAmount = (reg as any).tickets_amount ?? 0;
        const specialAmount = (reg as any).special_request_amount ?? 0;

        // event_registrations uses 'status' - confirmed = paid
        if (reg.status === 'confirmed') {
          paidCount++;
          registrationRevenuePaid += regAmount;
          extrasRevenuePaid += extrasAmount;
          ticketsRevenuePaid += ticketsAmount;
          specialRequestRevenuePaid += specialAmount;
        } else {
          pendingCount++;
          registrationRevenuePending += regAmount;
          extrasRevenuePending += extrasAmount;
          ticketsRevenuePending += ticketsAmount;
          specialRequestRevenuePending += specialAmount;
        }
      });

      // Process NEW registrations (registrations table) - parse pricing_breakdown
      newRegs?.forEach(reg => {
        const breakdown = reg.pricing_breakdown as any || {};
        // pricing_breakdown stores in cents, convert to dollars
        const regAmount = (breakdown.base_price || 0) / 100;
        const extrasAmount = (breakdown.extras || []).reduce((sum: number, e: any) => sum + ((e.price || 0) * (e.quantity || 1)), 0) / 100;
        const ticketsAmount = (breakdown.spectator_tickets || breakdown.tickets || []).reduce((sum: number, t: any) => sum + ((t.price || 0) * (t.quantity || 1)), 0) / 100;
        const specialAmount = (breakdown.special_request_fee || breakdown.schedule_request_fee || 0) / 100;

        // registrations table uses payment_status
        if (reg.payment_status === 'paid') {
          paidCount++;
          registrationRevenuePaid += regAmount;
          extrasRevenuePaid += extrasAmount;
          ticketsRevenuePaid += ticketsAmount;
          specialRequestRevenuePaid += specialAmount;
        } else {
          pendingCount++;
          registrationRevenuePending += regAmount;
          extrasRevenuePending += extrasAmount;
          ticketsRevenuePending += ticketsAmount;
          specialRequestRevenuePending += specialAmount;
        }
      });

      const totalPaid = registrationRevenuePaid + extrasRevenuePaid + ticketsRevenuePaid + specialRequestRevenuePaid;
      const totalPending = registrationRevenuePending + extrasRevenuePending + ticketsRevenuePending + specialRequestRevenuePending;

      const totalRegs = (oldRegs?.length || 0) + (newRegs?.length || 0);

      setSummary({
        registrationRevenue: registrationRevenuePaid,
        registrationPending: registrationRevenuePending,
        ticketRevenue: ticketsRevenuePaid,
        ticketPending: ticketsRevenuePending,
        extrasRevenue: extrasRevenuePaid,
        extrasPending: extrasRevenuePending,
        specialRequestRevenue: specialRequestRevenuePaid,
        specialRequestPending: specialRequestRevenuePending,
        totalRevenue: totalPaid,
        totalPending: totalPending,
        totalRegistrations: totalRegs,
        paidRegistrations: paidCount,
        pendingRegistrations: pendingCount,
        ticketsSold: tickets?.length || 0,
        extrasSold: extras?.length || 0
      });

      // Build purchased items list
      const items: PurchasedItem[] = [];

      // First add items from purchased_tickets table
      tickets?.forEach(ticket => {
        const regJoin = (ticket as any).registrations;
        const isPaid =
          regJoin?.payment_status === 'paid' ||
          regJoin?.status === 'paid' ||
          regJoin?.status === 'confirmed';

        items.push({
          id: ticket.id,
          type: 'ticket',
          name: ticket.ticket_name || 'Spectator Ticket',
          price: (ticket.price_paid || 0) / 100,
          quantity: 1,
          purchaser_name: ticket.purchaser_name || 'Unknown',
          purchaser_email: ticket.purchaser_email || '',
          is_redeemed: ticket.is_redeemed || false,
          redeemed_at: ticket.redeemed_at,
          qr_code: ticket.qr_code || '',
          created_at: ticket.created_at,
          registration_id: ticket.registration_id,
          payment_status: isPaid ? 'paid' : 'pending',
        });
      });

      // Add items from purchased_extras table
      extras?.forEach(extra => {
        const regJoin = (extra as any).registrations;
        const isPaid =
          regJoin?.payment_status === 'paid' ||
          regJoin?.status === 'paid' ||
          regJoin?.status === 'confirmed';

        items.push({
          id: extra.id,
          type: 'extra',
          name: extra.extra_name || 'Extra',
          price: (extra.price_paid || 0) / 100,
          quantity: extra.quantity || 1,
          purchaser_name: extra.purchaser_name || 'Unknown',
          purchaser_email: extra.purchaser_email || '',
          is_redeemed: extra.is_redeemed || false,
          redeemed_at: extra.redeemed_at,
          qr_code: extra.qr_code || '',
          created_at: extra.created_at,
          registration_id: extra.registration_id,
          payment_status: isPaid ? 'paid' : 'pending',
        });
      });

      // Track which registration IDs already have purchased_* records
      const ticketRegIds = new Set(tickets?.map(t => t.registration_id) || []);
      const extraRegIds = new Set(extras?.map(e => e.registration_id) || []);

      // Also extract items from registrations.pricing_breakdown for registrations 
      // that don't have entries in purchased_* tables (legacy/embedded data)
      newRegs?.forEach(reg => {
        const breakdown = reg.pricing_breakdown as any || {};
        const registrantData = reg.registrant_data as any || {};
        const purchaserName = registrantData.coach?.name || registrantData.athlete?.name || 'Unknown';
        const purchaserEmail = registrantData.coach?.email || registrantData.athlete?.email || '';

        const isPaid =
          reg.payment_status === 'paid' ||
          reg.status === 'paid' ||
          reg.status === 'confirmed';

        // Add tickets from pricing_breakdown if not already in purchased_tickets
        if (!ticketRegIds.has(reg.id)) {
          const regTickets = breakdown.spectator_tickets || breakdown.tickets || [];
          regTickets.forEach((ticket: any, idx: number) => {
            const qty = ticket.quantity || 1;
            for (let i = 0; i < qty; i++) {
              items.push({
                id: `${reg.id}-ticket-${idx}-${i}`,
                type: 'ticket',
                name: ticket.name || 'Spectator Ticket',
                price: (ticket.price || 0) / 100,
                quantity: 1,
                purchaser_name: purchaserName,
                purchaser_email: purchaserEmail,
                is_redeemed: false,
                redeemed_at: null,
                qr_code: '',
                created_at: reg.created_at,
                registration_id: reg.id,
                payment_status: isPaid ? 'paid' : 'pending',
              });
            }
          });
        }

        // Add extras from pricing_breakdown if not already in purchased_extras
        if (!extraRegIds.has(reg.id)) {
          const regExtras = breakdown.extras || [];
          regExtras.forEach((extra: any, idx: number) => {
            const qty = extra.quantity || 1;
            for (let i = 0; i < qty; i++) {
              items.push({
                id: `${reg.id}-extra-${idx}-${i}`,
                type: 'extra',
                name: extra.name || 'Extra',
                price: (extra.price || 0) / 100,
                quantity: 1,
                purchaser_name: purchaserName,
                purchaser_email: purchaserEmail,
                is_redeemed: false,
                redeemed_at: null,
                qr_code: '',
                created_at: reg.created_at,
                registration_id: reg.id,
                payment_status: isPaid ? 'paid' : 'pending',
              });
            }
          });
        }
      });

      // Sort by created_at descending
      items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setPurchasedItems(items);

      // Build special requests list from registrations
      const requests: SpecialRequest[] = [];
      
      // Extract from NEW registrations (registrations table)
      newRegs?.forEach(reg => {
        const registrantData = reg.registrant_data as any || {};
        const breakdown = reg.pricing_breakdown as any || {};
        const customResponses = registrantData.custom_responses || {};
        
        // Check for special request in custom_responses
        const teamName = registrantData.team?.name || '';
        const divisionName = registrantData.team?.division_id || breakdown.division || '';
        const coachName = registrantData.coach?.name || '';
        const coachEmail = registrantData.coach?.email || '';
        
        // Look for special request fields in custom_responses
        Object.keys(customResponses).forEach(key => {
          if (key.includes('special_request_enabled') && 
              (customResponses[key] === true || customResponses[key] === 'true')) {
            // Find the corresponding details field
            const detailsKey = key.replace('_enabled', '_details');
            const requestDetails = customResponses[detailsKey] || 'Schedule request';
            
            // Get fee from breakdown
            const fee = (breakdown.special_request_fee || 0) / 100;
            
            if (fee > 0 || requestDetails) {
              requests.push({
                id: `${reg.id}-${key}`,
                team_name: teamName,
                division: divisionName,
                coach_name: coachName,
                coach_email: coachEmail,
                request_details: requestDetails,
                fee: fee,
                payment_status: reg.payment_status === 'paid' ? 'paid' : 'pending',
                created_at: reg.created_at
              });
            }
          }
        });
      });
      
      // Sort by created_at descending
      requests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setSpecialRequests(requests);

      // Fetch coach registrations from event_coaches table
      const { data: coachRegs } = await supabase
        .from('event_coaches')
        .select('*')
        .eq('event_id', eventId);

      const coaches: CoachRegistration[] = (coachRegs || []).map((coach) => {
        const rawMetadata: unknown = (coach as any).metadata;
        let metadata: Record<string, any> = {};
        if (rawMetadata && typeof rawMetadata === 'object') {
          metadata = rawMetadata as Record<string, any>;
        } else if (typeof rawMetadata === 'string') {
          try {
            metadata = JSON.parse(rawMetadata) as Record<string, any>;
          } catch {
            metadata = {};
          }
        }

        // Stored in cents (jsonb) for paid registrations
        const amountPaidCents =
          Number(
            metadata.amount_paid ??
              metadata.total_amount ??
              metadata.amountPaid ??
              metadata.totalAmount ??
              0,
          ) || 0;

        // Extract additional coaches from metadata
        const additionalCoaches: string[] = metadata.additional_coaches || [];
        const additionalCoachCount = additionalCoaches.length || metadata.additional_coach_qty || metadata.additional_coach_count || 0;

        return {
          id: coach.id,
          coach_name: coach.coach_name || 'Unknown',
          coach_email: coach.coach_email || '',
          college_name: coach.college_name || '',
          division: coach.division || metadata.division || '',
          payment_status: coach.payment_status === 'paid' ? 'paid' : 'pending',
          amount: amountPaidCents / 100,
          created_at: coach.created_at,
          filler_name: metadata.filler_name || '',
          filler_email: metadata.filler_email || '',
          additional_coaches: additionalCoaches,
          additional_coach_count: additionalCoachCount,
        };
      });
      coaches.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setCoachRegistrations(coaches);

      // Fetch media credentials (from media_credentials table if exists)
      const { data: mediaRegs } = await supabase
        .from('media_credentials')
        .select('*')
        .eq('event_id', eventId);

      const media: MediaCredential[] = (mediaRegs || []).map(m => ({
        id: m.id,
        name: m.name || 'Unknown',
        email: m.email || '',
        organization: m.organization || '',
        credential_type: m.coverage_type || 'Media',
        status: m.status || 'pending',
        payment_status: m.payment_status || 'pending',
        amount_paid: m.amount_paid || 0,
        created_at: m.created_at
      }));
      media.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setMediaCredentials(media);

    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = purchasedItems.filter(item => {
    if (filterType === 'all') return true;
    return item.type === filterType;
  });

  const exportToCSV = () => {
    const headers = ['Type', 'Name', 'Purchaser', 'Email', 'Price', 'Quantity', 'Payment Status', 'Redeemed', 'Date'];
    const rows = filteredItems.map(item => [
      item.type,
      item.name,
      item.purchaser_name,
      item.purchaser_email,
      `$${item.price.toFixed(2)}`,
      item.quantity,
      item.payment_status === 'paid' ? 'Paid' : 'Pending',
      item.is_redeemed ? 'Yes' : 'No',
      format(new Date(item.created_at), 'yyyy-MM-dd')
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-tickets-extras-${eventId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportSpecialRequestsToCSV = () => {
    const headers = ['Team', 'Division', 'Coach', 'Email', 'Request Details', 'Fee', 'Payment Status', 'Date'];
    const rows = specialRequests.map(request => [
      request.team_name || 'N/A',
      request.division || 'N/A',
      request.coach_name,
      request.coach_email,
      `"${(request.request_details || '').replace(/"/g, '""')}"`,
      `$${request.fee.toFixed(2)}`,
      request.payment_status,
      format(new Date(request.created_at), 'yyyy-MM-dd')
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-special-requests-${eventId}.csv`;
    a.click();
  };

  const exportCoachesToCSV = () => {
    const headers = ['Coach Name', 'Coach Email', 'College', 'Division', 'Amount', 'Payment Status', 'Filler Name', 'Filler Email', 'Additional Coaches', 'Number of Additional Coaches', 'Date'];
    const rows = coachRegistrations.map(coach => [
      coach.coach_name,
      coach.coach_email,
      coach.college_name,
      coach.division,
      `$${coach.amount.toFixed(2)}`,
      coach.payment_status,
      coach.filler_name || '',
      coach.filler_email || '',
      `"${(coach.additional_coaches || []).join(', ')}"`,
      coach.additional_coach_count || 0,
      format(new Date(coach.created_at), 'yyyy-MM-dd')
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-coaches-${eventId}.csv`;
    a.click();
  };

  const exportMediaToCSV = () => {
    const headers = ['Name', 'Email', 'Organization', 'Credential Type', 'Status', 'Payment Status', 'Amount Paid', 'Date'];
    const rows = mediaCredentials.map(m => [
      m.name,
      m.email,
      m.organization,
      m.credential_type,
      m.status,
      m.payment_status,
      `$${(m.amount_paid / 100).toFixed(2)}`,
      format(new Date(m.created_at), 'yyyy-MM-dd')
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-media-credentials-${eventId}.csv`;
    a.click();
  };

  const exportAllToCSV = () => {
    exportToCSV();
    exportSpecialRequestsToCSV();
    if (coachRegistrations.length > 0) exportCoachesToCSV();
    if (mediaCredentials.length > 0) exportMediaToCSV();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards - Row 1 */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${summary?.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">All confirmed payments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              ${summary?.totalPending.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">Awaiting payment</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Registrations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-green-600">Paid: ${summary?.registrationRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-yellow-600">Pending: ${summary?.registrationPending.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.paidRegistrations || 0} paid / {summary?.pendingRegistrations || 0} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Extras</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-green-600">Paid: ${summary?.extrasRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-yellow-600">Pending: ${summary?.extrasPending.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{summary?.extrasSold || 0} extras sold</p>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards - Row 2 */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tickets</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-green-600">Paid: ${summary?.ticketRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-yellow-600">Pending: ${summary?.ticketPending.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{summary?.ticketsSold || 0} tickets sold</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Special Requests</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-green-600">Paid: ${summary?.specialRequestRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-yellow-600">Pending: ${summary?.specialRequestPending.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Purchased Items & Special Requests Tabs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Purchases & Special Requests</CardTitle>
          <Button variant="outline" size="sm" onClick={exportAllToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Download All
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <TabsList className="flex-wrap">
                <TabsTrigger value="items" className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Tickets & Extras
                </TabsTrigger>
                <TabsTrigger value="special" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Special Requests ({specialRequests.length})
                </TabsTrigger>
                <TabsTrigger value="coaches" className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Coaches ({coachRegistrations.length})
                </TabsTrigger>
                <TabsTrigger value="media" className="flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Media ({mediaCredentials.length})
                </TabsTrigger>
              </TabsList>
              
              {activeTab === 'items' && (
                <div className="flex gap-2">
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Items</SelectItem>
                      <SelectItem value="ticket">Tickets Only</SelectItem>
                      <SelectItem value="extra">Extras Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={exportToCSV}>
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              )}
              
              {activeTab === 'special' && (
                <Button variant="outline" size="sm" onClick={exportSpecialRequestsToCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}

              {activeTab === 'coaches' && (
                <Button variant="outline" size="sm" onClick={exportCoachesToCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}

              {activeTab === 'media' && (
                <Button variant="outline" size="sm" onClick={exportMediaToCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
            </div>

            <TabsContent value="items">
              {filteredItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No purchased items found for this event
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Purchaser</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Badge variant={item.type === 'ticket' ? 'default' : 'secondary'}>
                            {item.type === 'ticket' ? (
                              <><Ticket className="h-3 w-3 mr-1" /> Ticket</>
                            ) : (
                              <><Package className="h-3 w-3 mr-1" /> Extra</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">{item.purchaser_name}</div>
                          <div className="text-xs text-muted-foreground">{item.purchaser_email}</div>
                        </TableCell>
                        <TableCell>${item.price.toFixed(2)}</TableCell>
                        <TableCell>
                          {item.payment_status === 'paid' ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" /> Paid
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600">
                              <Clock className="h-3 w-3 mr-1" /> Pending
                            </Badge>
                          )}
                          {item.is_redeemed && (
                            <Badge variant="outline" className="text-blue-600 ml-1">
                              Redeemed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(item.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedItem(item);
                              setIsDetailOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="special">
              {specialRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No special requests found for this event
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team</TableHead>
                      <TableHead>Division</TableHead>
                      <TableHead>Coach</TableHead>
                      <TableHead>Request Details</TableHead>
                      <TableHead>Fee</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {specialRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">{request.team_name || 'N/A'}</TableCell>
                        <TableCell>{request.division || 'N/A'}</TableCell>
                        <TableCell>
                          <div className="text-sm">{request.coach_name}</div>
                          <div className="text-xs text-muted-foreground">{request.coach_email}</div>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <p className="truncate text-sm" title={request.request_details}>
                            {request.request_details || 'Schedule request'}
                          </p>
                        </TableCell>
                        <TableCell>${request.fee.toFixed(2)}</TableCell>
                        <TableCell>
                          {request.payment_status === 'paid' ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" /> Paid
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600">
                              <Clock className="h-3 w-3 mr-1" /> Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(request.created_at), 'MMM d, yyyy')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="coaches">
              {coachRegistrations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No coach registrations found for this event
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Coach</TableHead>
                      <TableHead>College</TableHead>
                      <TableHead>Division</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Filler</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coachRegistrations.map((coach) => (
                      <TableRow key={coach.id}>
                        <TableCell>
                          <div className="text-sm font-medium">{coach.coach_name}</div>
                          <div className="text-xs text-muted-foreground">{coach.coach_email}</div>
                        </TableCell>
                        <TableCell>{coach.college_name || 'N/A'}</TableCell>
                        <TableCell>{coach.division || 'N/A'}</TableCell>
                        <TableCell>${coach.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          {coach.payment_status === 'paid' ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" /> Paid
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600">
                              <Clock className="h-3 w-3 mr-1" /> Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {coach.filler_name ? (
                            <div>
                              <div className="text-sm">{coach.filler_name}</div>
                              <div className="text-xs text-muted-foreground">{coach.filler_email}</div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(coach.created_at), 'MMM d, yyyy')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="media">
              {mediaCredentials.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No media credentials found for this event
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Credential Type</TableHead>
                      <TableHead>Amount Paid</TableHead>
                      <TableHead>Payment Status</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mediaCredentials.map((media) => (
                      <TableRow key={media.id}>
                        <TableCell>
                          <div className="text-sm font-medium">{media.name}</div>
                          <div className="text-xs text-muted-foreground">{media.email}</div>
                        </TableCell>
                        <TableCell>{media.organization || 'N/A'}</TableCell>
                        <TableCell>{media.credential_type}</TableCell>
                        <TableCell>
                          ${(media.amount_paid / 100).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {media.payment_status === 'paid' ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" /> Paid
                            </Badge>
                          ) : media.payment_status === 'free' ? (
                            <Badge variant="outline" className="text-blue-600">
                              Free
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600">
                              <Clock className="h-3 w-3 mr-1" /> Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {media.status === 'approved' ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" /> Approved
                            </Badge>
                          ) : media.status === 'denied' ? (
                            <Badge variant="outline" className="text-red-600">
                              <XCircle className="h-3 w-3 mr-1" /> Denied
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600">
                              <Clock className="h-3 w-3 mr-1" /> Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(media.created_at), 'MMM d, yyyy')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Item Details</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{selectedItem.type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Item Name</p>
                  <p className="font-medium">{selectedItem.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Purchaser</p>
                  <p className="font-medium">{selectedItem.purchaser_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedItem.purchaser_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Price</p>
                  <p className="font-medium">${selectedItem.price.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{selectedItem.quantity}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={selectedItem.is_redeemed ? 'default' : 'secondary'}>
                    {selectedItem.is_redeemed ? 'Redeemed' : 'Valid'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Purchase Date</p>
                  <p className="font-medium">
                    {format(new Date(selectedItem.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              </div>
              {selectedItem.qr_code && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">QR Code</p>
                  <code className="text-xs bg-muted p-2 rounded block break-all">
                    {selectedItem.qr_code}
                  </code>
                </div>
              )}
              {selectedItem.redeemed_at && (
                <div>
                  <p className="text-sm text-muted-foreground">Redeemed At</p>
                  <p className="font-medium">
                    {format(new Date(selectedItem.redeemed_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventFinanceTab;
